import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { fetchIntegrations, getCachedIntegrations, IntegrationListItem } from "../lib/catalog";
import { ArrowUp, ArrowRight, KeyRound, Check, Copy, Zap, Sparkles, Search, Globe, Plug, ShieldCheck, SquarePen, Folder, Clock, Library, Blocks, Plus, ChevronRight, ChevronLeft, Landmark, PanelLeft, Users, LayoutGrid, UserRound, Send, Share2, Contact, Linkedin, MailCheck, Layers, Flame, Video, ShieldAlert, Eye, ScanLine, Cpu, Lightbulb, Workflow, CalendarClock, BadgeCheck, PenTool, Route, LineChart, FileSearch, FileText, Bot, PhoneCall, GraduationCap, Handshake, Gift, Filter, Signature, Network, MessageCircle, Radio, Inbox, PenSquare, Headset, Database, DatabaseZap, Target, RefreshCw, Magnet, LayoutTemplate, Megaphone, BarChart3, TrendingUp, BoxIcon } from "lucide-react";
// "Giga for ChatGPT" — ALTERNATE, MARKETING cut. Positions Giga as the
// marketing operating system layered onto ChatGPT. Leads with integration
// coverage: a segmented "how many connectors" comparison, then the full
// ontology of marketing tool categories Giga covers, then how easy adding one
// is. Built on the Giga design tokens (Tailwind: brand / ink / sand / cream).

const OPENAI_ICON = 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg';
const GIGA_MARK = "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-mark.png";
const GMAIL_ICON = 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/gmail.svg';

// Segmented coverage meters (out of 10). ChatGPT alone is thin; Giga maxes it.
const METERS = [{
  metric: 'Available connectors',
  rows: [{
    who: 'ChatGPT',
    score: 3,
    brand: false
  }, {
    who: 'Giga',
    score: 10,
    brand: true
  }]
}];

// The ontology — the marketing tool categories Giga spans. Mirrors a
// "tools by category" wall; illustrative, expand from the real catalog.
const MARKETING_CATEGORIES = [{
  name: 'Email Finder',
  icon: Search
}, {
  name: 'Email Sender',
  icon: Send
}, {
  name: 'Multichannel Outreach',
  icon: Share2
}, {
  name: 'CRMs',
  icon: Contact
}, {
  name: 'LinkedIn Automation',
  icon: Linkedin
}, {
  name: 'Deliverability',
  icon: MailCheck
}, {
  name: 'Waterfall Enrichment',
  icon: Layers
}, {
  name: 'Email Warmer',
  icon: Flame
}, {
  name: 'Video Outreach',
  icon: Video
}, {
  name: 'Spam Testing',
  icon: ShieldAlert
}, {
  name: 'Visitor Identification',
  icon: Eye
}, {
  name: 'Scraping',
  icon: ScanLine
}, {
  name: 'Technology Lookup',
  icon: Cpu
}, {
  name: 'Prospect Insights',
  icon: Lightbulb
}, {
  name: 'Workflow Automation',
  icon: Workflow
}, {
  name: 'Social Scheduling',
  icon: CalendarClock
}, {
  name: 'Email Verification',
  icon: BadgeCheck
}, {
  name: 'AI Writing Assistants',
  icon: PenTool
}, {
  name: 'Attribution',
  icon: Route
}, {
  name: 'Sales Reporting',
  icon: LineChart
}, {
  name: 'Account Research',
  icon: FileSearch
}, {
  name: 'Proposals',
  icon: FileText
}, {
  name: 'AI Avatars',
  icon: Bot
}, {
  name: 'Phone Outreach',
  icon: PhoneCall
}, {
  name: 'Sales Coaching',
  icon: GraduationCap
}, {
  name: 'Warm Intros',
  icon: Handshake
}, {
  name: 'Prospect Gifting',
  icon: Gift
}, {
  name: 'Sales Funnel',
  icon: Filter
}, {
  name: 'E-Signature',
  icon: Signature
}, {
  name: 'Network Growth',
  icon: Network
}, {
  name: 'Buyer Engagement',
  icon: MessageCircle
}, {
  name: 'Social Listening',
  icon: Radio
}, {
  name: 'Inbox Management',
  icon: Inbox
}, {
  name: 'Copywriting',
  icon: PenSquare
}, {
  name: 'Customer Service',
  icon: Headset
}, {
  name: 'Prospecting Database',
  icon: Database
}, {
  name: 'Data Enrichment',
  icon: DatabaseZap
}, {
  name: 'Intent Signals',
  icon: Target
}, {
  name: 'CRM Syncing',
  icon: RefreshCw
}, {
  name: 'Lead Generation',
  icon: Magnet
}, {
  name: 'Landing Pages',
  icon: LayoutTemplate
}, {
  name: 'Ad Management',
  icon: Megaphone
}, {
  name: 'Analytics',
  icon: BarChart3
}, {
  name: 'SEO',
  icon: TrendingUp
}];

// ── "Built for teams" — the same sharing works for anything ────────
// The visual rotates a credential → file → skill; the tick marks move to
// whoever each is shared with. Spaces stay deliberately varied (a project, an
// area, an exploration).
const SHARE_TARGETS = [{
  key: 'Groups',
  icon: Users,
  people: false,
  items: ['Marketing team', 'Customer Support', 'Legal', 'Finance']
}, {
  key: 'Spaces',
  icon: LayoutGrid,
  people: false,
  items: ['Q3 Product Launch', 'Website Redesign', 'Early User Activation', 'Competitor Research', 'EMEA Expansion']
}, {
  key: 'Individuals',
  icon: UserRound,
  people: true,
  items: ['Priya Patel', 'Tom Nguyen', 'Sarah Chen', 'James Okafor']
}];

// Each rotates in at the top; `shares` lists which SHARE_TARGETS items it's
// shared with (ticked). `logo` is an image; otherwise `icon` is a lucide glyph.
const SHAREABLES = [{
  type: 'Credential',
  logo: GMAIL_ICON,
  name: 'Gmail inbox',
  sub: 'support@getgiga.com',
  shares: {
    Groups: ['Customer Support'],
    Spaces: ['Early User Activation'],
    Individuals: ['Tom Nguyen', 'Sarah Chen']
  }
}, {
  type: 'File',
  icon: FileText,
  name: '2025 Marketing Report',
  sub: 'PDF · 4.2 MB',
  shares: {
    Groups: ['Marketing team'],
    Spaces: ['Q3 Product Launch'],
    Individuals: ['Priya Patel']
  }
}, {
  type: 'Skill',
  icon: Sparkles,
  name: 'Competitor Analysis',
  sub: 'Custom skill',
  shares: {
    Groups: ['Marketing team', 'Finance'],
    Spaces: ['Competitor Research'],
    Individuals: ['James Okafor']
  }
}];

// ── Embedded expertise — Giga's structured answer vs ChatGPT's ─────
const UGC_SKILLS = [{
  initials: 'CH',
  name: 'Corey Haynes',
  skill: 'UGC growth loops',
  color: '#B0451E'
}, {
  initials: 'JP',
  name: 'Julia Pintar',
  skill: 'Creator sourcing & briefs',
  color: '#6D28D9'
}, {
  initials: 'DD',
  name: 'Dara Denney',
  skill: 'UGC ad creative',
  color: '#1E293B'
}];

// ── "Never hit a dead end" — Giga finds/builds a missing integration ──
const DEADEND_CHECKS = [{
  icon: Search,
  label: 'Checked the Giga directory',
  status: 'Not listed yet',
  tone: 'muted'
}, {
  icon: Globe,
  label: "Searched the web for Larkspur's API",
  status: 'Found',
  tone: 'green'
}, {
  icon: KeyRound,
  label: 'Detected auth — OAuth 2.0',
  status: 'Ready',
  tone: 'green'
}, {
  icon: Plug,
  label: 'Built the integration',
  status: 'Done',
  tone: 'green'
}];

// Which catalog tools ChatGPT can reach on its own (the handful that speak
// MCP / ship as first-party connectors). Illustrative — matched loosely by
// name/slug so the toggle has a believable "few lit, most dim" split.
const CHATGPT_MCP_KEYS = ['gmail', 'googledrive', 'google drive', 'googlecalendar', 'google calendar', 'github', 'slack', 'notion', 'dropbox', 'box', 'linear', 'hubspot', 'asana', 'atlassian', 'jira', 'confluence', 'canva', 'figma', 'stripe', 'intercom', 'sharepoint', 'onedrive', 'outlook', 'monday', 'zapier'];
function availableInChatGPT(item: IntegrationListItem): boolean {
  const hay = `${item.display_name} ${item.slug}`.toLowerCase();
  return CHATGPT_MCP_KEYS.some(k => hay.includes(k));
}

// The catalog API has NO popularity field and returns tools alphabetically (so
// the raw top is obscure "0codekit"-type apps). This is a curated, marketing-
// first ranking of real catalog slugs; matches float to the top in this order,
// everything else keeps the API's alphabetical order after them.
const POPULAR_SLUGS = ['hubspot', 'salesforce-rest-api', 'mailchimp', 'klaviyo-oauth', 'google-ads', 'meta-ads', 'facebook', 'linkedin', 'tiktok-com', 'stripe', 'slack', 'notion', 'intercom', 'zendesk-com', 'pipedrive-com', 'apollo', 'salesloft', 'marketo', 'attio', 'typeform-com-openapi', 'webflow', 'wordpress', 'canva', 'segment-com', 'analytics-googleapis-com', 'amplitude', 'posthog', 'sendgrid', 'github', 'linear', 'asana', 'jira', 'monday', 'clickup', 'todoist-com', 'airtable', 'zapier', 'calendly', 'zoom', 'microsoft-teams', 'google-drive', 'google-calendar', 'sheets-googleapis-com', 'googleapis-com-gmail-gmail', 'dropbox', 'sharepoint', 'confluence', 'sentry', 'discord-com', 'xero', 'freshdesk-com', 'snowflake-com-openapi'];
const POPULAR_RANK = new Map(POPULAR_SLUGS.map((s, i) => [s, i]));

// A 10-segment meter bar that fills, segment-by-segment, when it scrolls into
// view. The filled segments stagger in left→right so the bar reads as "filling
// up"; `delay` lets the Giga row start after ChatGPT's for a payoff beat.
const SEGMENT = {
  hidden: {
    opacity: 0,
    scaleX: 0.25
  },
  visible: {
    opacity: 1,
    scaleX: 1,
    transition: {
      duration: 0.34,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
function Meter({
  score,
  brand,
  delay = 0
}: {
  score: number;
  brand: boolean;
  delay?: number;
}) {
  return <motion.div className="flex gap-1" initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-12% 0px -12% 0px'
  }} transition={{
    staggerChildren: 0.07,
    delayChildren: delay
  }} data-id="element-9579" data-mp="36teg6yqls7d9">
      {Array.from({
      length: 10
    }).map((_, i) => i < score ? <motion.div key={i} variants={SEGMENT} className={['h-2.5 flex-1 rounded-[3px] origin-left', brand ? 'bg-brand' : 'bg-ink/60'].join(' ')} data-id="element-9580" data-mp="3kjqa936r0g71" /> : <div key={i} className="h-2.5 flex-1 rounded-[3px] bg-sand-100" data-id="element-9581" data-mp="2nv22r0snsbbv" />)}
    </motion.div>;
}

// Styled tooltip (no library). Renders into document.body via a portal so it's
// never clipped by the grid's scroll container, and is positioned top-center
// above the trigger using its live viewport rect. Nothing renders until hover,
// so it's SSR/prerender-safe.
function Tip({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({
      x: r.left + r.width / 2,
      y: r.top
    });
  };
  const hide = () => setPos(null);
  return <span ref={ref} onMouseEnter={show} onMouseLeave={hide} className="relative inline-flex" data-id="element-9582" data-mp="2x3yx2w8dnbjp">
      {children}
      {pos && typeof document !== 'undefined' && createPortal(<div style={{
      position: 'fixed',
      left: pos.x,
      top: pos.y,
      transform: 'translate(-50%, calc(-100% - 9px))'
    }} className="pointer-events-none z-[100] whitespace-nowrap rounded-md bg-ink text-white text-[11px] font-medium leading-none px-2 py-1.5 shadow-lg" data-id="element-9583" data-mp="24fjjme4w91ah">
          {label}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-ink" data-id="element-9584" data-mp="2if2s581maenb" />
        </div>, document.body)}
    </span>;
}

// A real integration from the Giga catalog (logo + name + first category).
// Two support marks sit in the top-right: Giga (always lit — everything works
// in Giga) and ChatGPT (faded when it can't reach the tool). Falls back to an
// initial tile when a logo is missing or fails to load.
function IntegrationTile({
  item,
  available
}: {
  item: IntegrationListItem;
  available: boolean;
}) {
  const [broken, setBroken] = useState(false);
  return <div className="relative flex items-center gap-3 p-3 rounded-xl bg-cream border border-sand-200 min-w-0" data-id="element-9585" data-mp="3q7je6c75pi4m">
      {item.logo_url && !broken ? <img src={item.logo_url} alt="" loading="lazy" onError={() => setBroken(true)} className="w-8 h-8 object-contain rounded flex-none" data-id="element-9586" data-mp="3l9hvvthk1mu7" /> : <div className="w-8 h-8 rounded-lg bg-brand/10 text-brand-dark flex items-center justify-center text-sm font-bold flex-none" data-id="element-9587" data-mp="3b9r4i0oajnyc">{item.display_name.charAt(0).toUpperCase()}</div>}
      <div className="min-w-0 flex flex-col pr-10" data-id="element-9588" data-mp="37a8ljag15my5">
        <span className="text-[13px] font-semibold text-ink truncate" data-id="element-9589" data-mp="19hcnr4xhgpji">{item.display_name}</span>
        {item.categories[0] && <span className="text-[10px] text-ink-faint uppercase tracking-wider truncate" data-id="element-9590" data-mp="15hakwsg0hq2n">{item.categories[0].name}</span>}
      </div>
      {/* Support marks — Giga always, ChatGPT faded when unsupported */}
      <div className="absolute top-2 right-2 flex items-center gap-1.5" data-id="element-9591" data-mp="33rsvrydxogw6">
        <Tip label="Works in Giga" data-id="element-9592" data-mp="2pzuv8byaftbm">
          <img src={GIGA_MARK} alt="" className="w-3.5 h-3.5 rounded-[2px]" data-id="element-9593" data-mp="2pwqigq6rbp9c" />
        </Tip>
        <Tip label={available ? 'Works in ChatGPT' : "Doesn't work in ChatGPT"} data-id="element-9594" data-mp="2u0oj55bqd84i">
          <img src={OPENAI_ICON} alt="" className={['w-3.5 h-3.5 transition-opacity', available ? '' : 'opacity-20 grayscale'].join(' ')} data-id="element-9595" data-mp="3hraoupy9igbc" />
        </Tip>
      </div>
    </div>;
}
function MeterRow({
  who,
  score,
  brand
}: {
  who: string;
  score: number;
  brand: boolean;
}) {
  const logo = who === 'Giga' ? GIGA_MARK : OPENAI_ICON;
  return <div className="grid grid-cols-[92px_1fr] md:grid-cols-[120px_1fr] items-center gap-3 md:gap-4" data-id="element-9596" data-mp="3dq8m3z54zm4m">
      <span className="inline-flex items-center gap-2 text-[13px] font-medium text-ink" data-id="element-9597" data-mp="2298y3fptntge">
        <img src={logo} alt="" className={['w-4 h-4', who === 'Giga' ? 'rounded-[3px]' : ''].join(' ')} data-id="element-9598" data-mp="226w7ke600arq" />
        {who}
      </span>
      <Meter score={score} brand={brand} delay={brand ? 0.35 : 0.1} data-id="element-9599" data-mp="1pr6blv5xy5rp" />
    </div>;
}

// Rotating "share anything" visual — a credential, then a file, then a skill,
// animating in/out at the top while the tick marks below move to whoever each
// item is shared with.
export function TeamSharingVisual() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive(i => (i + 1) % SHAREABLES.length), 3200);
    return () => clearInterval(id);
  }, []);
  const item = SHAREABLES[active];
  const shared = (key: string, label: string) => (item.shares as Record<string, string[]>)[key]?.includes(label);
  return <div className="flex flex-col items-center" data-id="element-9600" data-mp="1fziou7kbxcwk">
      {/* The rotating shareable */}
      <div className="flex items-center justify-center w-full h-[76px]" data-id="element-9601" data-mp="s9odxvjklyey">
        <AnimatePresence mode="wait" data-id="element-9602" data-mp="nb4p9ndle8iw">
          <motion.div key={item.type} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} className="inline-flex items-center gap-3 bg-white border border-sand-200 rounded-2xl shadow-[0_6px_24px_rgba(43,33,26,0.08)] pl-3 pr-3.5 py-2.5" data-id="element-9603" data-mp="na1zjis0ac4k">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-sand-50 border border-sand-200 flex-none" data-id="element-9604" data-mp="9bsdp8y3463z">
              {item.logo ? <img src={item.logo} alt="" className="w-6 h-6" data-id="element-9605" data-mp="e8iujyhpr5qg" /> : <item.icon className="w-5 h-5 text-brand-dark" data-id="element-9606" data-mp="j96sl44d0mu6" />}
            </span>
            <div className="flex flex-col text-left pr-1" data-id="element-9607" data-mp="narzm8u7q6wg">
              <span className="font-title font-bold text-[15px] leading-tight" data-id="element-9608" data-mp="na8tdpdxd6y9">{item.name}</span>
              <span className="font-mono text-[11.5px] text-ink-muted" data-id="element-9609" data-mp="2l2mke4c6o7qq">{item.sub}</span>
            </div>
            <span className="inline-flex items-center bg-cream border border-sand-200 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-ink-muted flex-none" data-id="element-9610" data-mp="2p2okwa66mv4f">{item.type}</span>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Connector */}
      <div className="w-0 h-8 border-l-2 border-dashed border-[#D6CCC0]" data-id="element-9611" data-mp="27tcbhws50i7j" />

      {/* Share-with targets */}
      <div className="relative w-full border-2 border-dashed border-[#D6CCC0] rounded-[24px] bg-sand-50 pt-9 px-4 md:px-6 pb-5 md:pb-6" data-id="element-9612" data-mp="23pebljuzn3df">
        <div className="absolute -top-3 left-6 md:left-8 flex items-center gap-2 bg-ink text-white rounded-full py-1.5 pl-3 pr-4" data-id="element-9613" data-mp="2vi5ay37yx86w">
          <Share2 className="w-3.5 h-3.5 text-brand-lighter" data-id="element-9614" data-mp="2rfqrpi0e06f3" />
          <span className="font-mono text-[11px] font-semibold tracking-[0.14em] uppercase" data-id="element-9615" data-mp="2mi8q6cop8th4">Share with</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4" data-id="element-9616" data-mp="1g2y32g2w7y39">
          {SHARE_TARGETS.map(({
          key,
          icon: Icon,
          items,
          people
        }) => <div key={key} className="bg-white border border-sand-200 rounded-2xl p-4 md:p-5 flex flex-col gap-3" data-id="element-9617" data-mp="1cjh65amwipou">
              <div className="flex items-center gap-2" data-id="element-9618" data-mp="1hjbtj47gwx55">
                <Icon className="w-4 h-4 text-brand-dark flex-none" data-id="element-9619" data-mp="1hgpp3dfvixr5" />
                <span className="font-title font-bold text-[15px]" data-id="element-9620" data-mp="13irfsmgetrii">{key}</span>
              </div>
              <div className="flex flex-col gap-1.5 items-start" data-id="element-9621" data-mp="17jb9fg1ryczw">
                {items.map(label => {
              const on = shared(key, label);
              return <span key={label} className={['inline-flex items-center gap-2 text-[12px] font-medium rounded-full pl-2.5 pr-2 py-1 border transition-colors duration-300', on ? 'bg-brand/[0.07] border-brand/40 text-ink' : 'bg-cream border-sand-200 text-ink'].join(' ')} data-id="element-9622" data-mp="ykpurvzfbi4e">
                      {people && <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-brand/10 text-brand-dark text-[8px] font-bold flex-none" data-id="element-9623" data-mp="auvibfj0nvko">{label.split(' ').map(w => w[0]).join('').slice(0, 2)}</span>}
                      <span data-id="element-9624" data-mp="3scizfkrzs0cc">{label}</span>
                      <span className="inline-flex items-center justify-center w-3.5 h-3.5 flex-none" data-id="element-9625" data-mp="3sdklslegk99d">
                        <AnimatePresence data-id="element-9626" data-mp="3ngc2uilhk172">
                          {on && <motion.span key="t" initial={{
                      opacity: 0,
                      scale: 0.3
                    }} animate={{
                      opacity: 1,
                      scale: 1
                    }} exit={{
                      opacity: 0,
                      scale: 0.3
                    }} transition={{
                      duration: 0.2
                    }} className="inline-flex" data-id="element-9627" data-mp="58uzkg5jlwuv">
                            <Check className="w-3.5 h-3.5 text-brand-dark" strokeWidth={2.5} data-id="element-9628" data-mp="ovsqoy50z96y" />
                          </motion.span>}
                        </AnimatePresence>
                      </span>
                    </span>;
            })}
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}

// A replica of the ChatGPT composer box — reused for the prompt (with a send
// button) and for the answer (without).
function ChatBox({
  send = false,
  children
}: {
  send?: boolean;
  children: React.ReactNode;
}) {
  return <div className="w-full bg-white border border-sand-200 rounded-[26px] shadow-[0_10px_44px_rgba(43,33,26,0.09)] py-4 pl-5 pr-3 flex items-center gap-3" data-id="element-9629" data-mp="29at4gy042obi">
      <div className="flex-1 text-[15px] md:text-[16px] leading-snug text-ink" data-id="element-9630" data-mp="2e8bfctxovdm5">{children}</div>
      {send && <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#0D0D0D] text-white flex-none" data-id="element-9631" data-mp="1hi25wguygnj3"><ArrowUp className="w-4 h-4" data-id="element-9632" data-mp="1lje06zorxwxm" /></span>}
    </div>;
}

// A small dashed vertical connector between the stacked flow rows.
function FlowLink() {
  return <span className="self-center w-0 h-11 border-l-2 border-dashed border-[#D6CCC0]" data-id="element-9633" data-mp="1vcbl1ch8nxze" />;
}

// ChatGPT-style "tool calling" indicator — cycles through Giga's tool-call
// statuses with the Giga mark and bouncing dots, the way a chat shows work.
const GIGA_STATUSES = ['Searching the Giga directory…', 'Calling Giga tools…', 'Pulling UGC integrations…', 'Finding operator skills…'];
function GigaWorking({
  statuses = GIGA_STATUSES,
  bare = false
}: {
  statuses?: string[];
  bare?: boolean;
}) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI(x => (x + 1) % statuses.length), 1600);
    return () => clearInterval(id);
  }, [statuses]);
  const label = <AnimatePresence mode="wait" data-id="element-9634" data-mp="cdx33ylx76it">
      <motion.span key={i} initial={{
      opacity: 0,
      y: 4
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -4
    }} transition={{
      duration: 0.2
    }} className={bare ? 'text-[14.5px] text-ink-faint whitespace-nowrap' : 'font-mono text-[11.5px] text-ink-muted whitespace-nowrap'} data-id="element-9635" data-mp="ceyjmilpgl7x">{statuses[i % statuses.length]}</motion.span>
    </AnimatePresence>;
  if (bare) return <div className="inline-flex items-center gap-2.5" data-id="element-9636" data-mp="3ksoyfg5iuf4f">
      <img src={GIGA_MARK} alt="" className="w-5 h-5 rounded-[5px] animate-pulse" data-id="element-9637" data-mp="3kt7413e2q5p0" />{label}
    </div>;
  return <div className="inline-flex self-start items-center gap-2 bg-cream border border-sand-200 rounded-full pl-2 pr-3 py-1.5" data-id="element-9638" data-mp="29op9d1clpfoy">
      <img src={GIGA_MARK} alt="" className="w-4 h-4 rounded-[3px] animate-pulse" data-id="element-9639" data-mp="25lswzsdnmftg" />{label}
      <span className="flex items-center gap-0.5" data-id="element-9640" data-mp="20qnaosgcaz4u">
        {[0, 150, 300].map(d => <span key={d} className="w-1 h-1 rounded-full bg-ink-faint animate-bounce" style={{
        animationDelay: `${d}ms`
      }} data-id="element-9641" data-mp="1voxwx51qg7d2" />)}
      </span>
    </div>;
}

// ── Getting started — a scroll-driven recreation of the ChatGPT plugin
// install flow. Three screens rebuilt in markup (chats/recents removed), the
// left frame swapping to the active step as the steps scroll past centre.
const CAPTIONS = ['Search the ChatGPT plugin store for Giga', 'Click Install'];
const FEATURED = [{
  name: 'Data Analytics',
  desc: 'Answer product and business questions',
  icon: BarChart3,
  color: '#7C5CFC'
}, {
  name: 'GitHub',
  desc: 'Triage PRs, issues, CI, and flows',
  logo: 'github'
}, {
  name: 'Investment Banking',
  desc: 'M&A, capital markets, valuation',
  icon: Landmark,
  color: '#3A6B4E'
}, {
  name: 'Google Drive',
  desc: 'Work across Drive, Docs, Sheets',
  logo: 'googledrive'
}];
function ChatGPTSidebar() {
  const items: [string, any, boolean][] = [['New chat', SquarePen, false], ['Projects', Folder, false], ['Scheduled', Clock, false], ['Library', Library, false], ['Plugins', Blocks, true], ['Agents', Bot, false], ['GPTs', BoxIcon, false]];
  return <div className="hidden sm:flex w-[150px] md:w-[176px] flex-none flex-col gap-0.5 bg-[#F9F9F9] border-r border-black/[0.06] py-3 px-2" data-id="element-9642" data-mp="f4nk7lmyacsg">
      <div className="flex items-center justify-between px-2 pb-3" data-id="element-9643" data-mp="k38l7na110hy">
        <span className="text-[13px] font-semibold text-[#0D0D0D]" data-id="element-9644" data-mp="owbhi366uo1u">ChatGPT</span>
        <span className="flex items-center gap-1.5 text-[#9A9A9A]" data-id="element-9645" data-mp="3nm1ys02t1pc4"><Search className="w-3.5 h-3.5" data-id="element-9646" data-mp="3njfse3rnhfvh" /><PanelLeft className="w-3.5 h-3.5" data-id="element-9647" data-mp="3sjtocnfz8zd5" /></span>
      </div>
      {items.map(([label, Icon, active]) => <span key={label} className={['flex items-center gap-2 rounded-lg px-2 py-1.5 text-[12.5px]', active ? 'bg-black/[0.06] font-medium text-[#0D0D0D]' : 'text-[#3D3D3D]'].join(' ')} data-id="element-9648" data-mp="3kj5iue08bhdv">
          <Icon className="w-4 h-4 text-[#5C5C5C]" data-id="element-9649" data-mp="3u9799cg21bvy" />{label}
        </span>)}
    </div>;
}
function ScreenBrowse({
  typed
}: {
  typed: string;
}) {
  return <div className="h-full overflow-hidden px-4 md:px-5 py-3.5 flex flex-col gap-3.5" data-id="element-9650" data-mp="3pavzt2to93e9">
      <div className="flex justify-center" data-id="element-9651" data-mp="31lkvxch0ysvw">
        <div className="inline-flex items-center gap-0.5 bg-black/[0.05] rounded-full p-0.5 text-[11.5px]" data-id="element-9652" data-mp="2wlq518x923au">
          <span className="px-3 py-1 rounded-full bg-white font-medium text-[#0D0D0D] shadow-sm" data-id="element-9653" data-mp="3bipktk2992ge">Plugins</span>
          <span className="px-3 py-1 text-[#6A6A6A]" data-id="element-9654" data-mp="36jcwsvzndpc8">Skills</span>
        </div>
      </div>
      <div className="flex items-start justify-between gap-3" data-id="element-9655" data-mp="qsi33rkdw46t">
        <div className="flex flex-col" data-id="element-9656" data-mp="quusuqwdviuf">
          <span className="text-[20px] font-bold text-[#0D0D0D] leading-tight" data-id="element-9657" data-mp="lu6qj92pffkw">Plugins</span>
          <span className="text-[12px] text-[#8A8A8A]" data-id="element-9658" data-mp="tdg0b3f4hgah">Work with ChatGPT across your favorite tools.</span>
        </div>
        <span data-cursor="search" className={['inline-flex items-center gap-1.5 rounded-full border w-[140px] overflow-hidden whitespace-nowrap px-2.5 py-1.5 text-[11px] flex-none', typed ? 'border-black/25 text-[#0D0D0D]' : 'border-black/10 text-[#9A9A9A]'].join(' ')} data-id="element-9659" data-mp="ycj5qoz0loiz"><Search className="w-3 h-3 text-[#9A9A9A] flex-none" data-id="element-9660" data-mp="1c3nzjafpbmle" />{typed ? <span data-id="element-9661" data-mp="1c47h2q1fw8yw">{typed}<span className="inline-block w-px h-[10px] bg-[#0D0D0D] align-middle ml-px animate-pulse" data-id="element-9662" data-mp="1c4ftkgtnhc9k" /></span> : 'Search plugins'}</span>
      </div>
      <div className="flex flex-col gap-1.5" data-id="element-9663" data-mp="13bvuxgdcz7oq">
        <span className="inline-flex items-center gap-0.5 text-[11.5px] font-semibold text-[#0D0D0D]" data-id="element-9664" data-mp="13b3xe007azvt">Installed <ChevronRight className="w-3 h-3 text-[#9A9A9A]" data-id="element-9665" data-mp="17b4ool8ef5ka" /></span>
        <div className="flex gap-1.5" data-id="element-9666" data-mp="1v1h2udu8lyjb">
          <img src={GIGA_MARK} alt="" className="w-8 h-8 rounded-lg flex-none" data-id="element-9667" data-mp="1v0fghd7rtpma" />
          {['#4285F4', '#E8710A', '#EA4335', '#F9AB00', '#0F9D58'].map((c, i) => <span key={i} className="w-8 h-8 rounded-lg border border-black/10 bg-white flex items-center justify-center flex-none" data-id="element-9668" data-mp="c56yhhjc30mj" data-mpi={i}><span className="w-3.5 h-3.5 rounded-[3px]" style={{
            background: c
          }} data-id="element-9669" data-mp="752jxx1zkfyy" /></span>)}
        </div>
      </div>
      <span className="inline-flex items-center gap-0.5 self-start bg-black/[0.05] rounded-full p-0.5 text-[11.5px]" data-id="element-9670" data-mp="28kfkyakijn5">
        <span className="px-2.5 py-0.5 rounded-full bg-white font-medium text-[#0D0D0D] shadow-sm" data-id="element-9671" data-mp="uv565wj7yrpn">Public</span>
        <span className="px-2.5 py-0.5 text-[#6A6A6A]" data-id="element-9672" data-mp="pvtq6pqofq94">Personal</span>
      </span>
      <div className="flex flex-col gap-2" data-id="element-9673" data-mp="l5daeyzdvsjl">
        <span className="inline-flex items-center gap-0.5 text-[11.5px] font-semibold text-[#0D0D0D]" data-id="element-9674" data-mp="l4aicnqbrk23">Featured <ChevronRight className="w-3 h-3 text-[#9A9A9A]" data-id="element-9675" data-mp="h3ia9xt6vl7e" /></span>
        <div className="grid grid-cols-2 gap-x-5 gap-y-3" data-id="element-9676" data-mp="1dmfxgesux6i2">
          {FEATURED.map(f => <div key={f.name} className="flex items-center gap-2 min-w-0" data-id="element-9677" data-mp="18v7mvffsjf3b">
              {f.logo ? <span className="w-7 h-7 rounded-lg border border-black/10 bg-white flex items-center justify-center flex-none" data-id="element-9678" data-mp="18tcxnoxhl0tf"><img src={`https://cdn.simpleicons.org/${f.logo}`} alt="" className="w-4 h-4" data-id="element-9679" data-mp="2wh0x8rxywfvx" /></span> : <span className="w-7 h-7 rounded-lg flex items-center justify-center flex-none" style={{
            background: f.color
          }} data-id="element-9680" data-mp="2wivf1xdxzrvs"><f.icon className="w-3.5 h-3.5 text-white" data-id="element-9681" data-mp="35hz19a8eonei" /></span>}
              <div className="flex flex-col min-w-0" data-id="element-9682" data-mp="35fcl4m7g8zko">
                <span className="text-[12px] font-medium text-[#0D0D0D] leading-tight" data-id="element-9683" data-mp="3ag0l3xdnoqr1">{f.name}</span>
                <span className="text-[11px] text-[#9A9A9A] truncate" data-id="element-9684" data-mp="3felizq5fqycb">{f.desc}</span>
              </div>
              <Plus className="w-3.5 h-3.5 text-[#B0B0B0] ml-auto flex-none" data-id="element-9685" data-mp="3k74ywtrli5ka" />
            </div>)}
        </div>
      </div>
    </div>;
}
function ScreenDetail({
  installHover
}: {
  installHover: boolean;
}) {
  return <div className="h-full overflow-hidden px-4 md:px-5 py-3.5 flex flex-col gap-3" data-id="element-9686" data-mp="3t94eriqvj3ln">
      <span className="inline-flex items-center gap-1 text-[12px] text-[#6A6A6A]" data-id="element-9687" data-mp="21i5naldy8xe"><ChevronLeft className="w-3.5 h-3.5" data-id="element-9688" data-mp="1y3d9iuz9qzf1" />Plugins</span>
      <img src={GIGA_MARK} alt="" className="w-12 h-12 rounded-xl" data-id="element-9689" data-mp="1y1s9wqy3kqab" />
      <div className="flex items-center justify-between gap-3" data-id="element-9690" data-mp="1t6mt26jjy1t3">
        <span className="text-[22px] font-bold text-[#0D0D0D]" data-id="element-9691" data-mp="27u7tfsp1lmxb">Giga</span>
        <span className="flex items-center gap-2 flex-none" data-id="element-9692" data-mp="2337ukhwd4us6">
          <span className="text-[#9A9A9A] text-[13px]" data-id="element-9693" data-mp="231wtwqy7s35a">•••</span>
          <span data-cursor="install" className={['inline-flex items-center bg-[#0D0D0D] text-white rounded-full px-4 py-1.5 text-[12px] font-medium origin-center transition-transform duration-200', installHover ? 'scale-[1.2] shadow-lg' : ''].join(' ')} data-id="element-9694" data-mp="2gtuweixf70sh">Install</span>
        </span>
      </div>
      <span className="text-[12px] text-[#8A8A8A] -mt-1.5" data-id="element-9695" data-mp="2ltfvctclb957">Company Operating System</span>
      <div className="flex flex-col gap-1.5" data-id="element-9696" data-mp="we9dr5zd02n4">
        <span className="text-[12.5px] font-semibold text-[#0D0D0D]" data-id="element-9697" data-mp="wf1c2okcceh3">App</span>
        <div className="flex items-center gap-2" data-id="element-9698" data-mp="rgzt03mx0f5m"><img src={GIGA_MARK} alt="" className="w-6 h-6 rounded-md" data-id="element-9699" data-mp="ri14g97uf3nv" /><span className="text-[12px] text-[#0D0D0D]" data-id="element-9700" data-mp="334cria6pk0pe">Giga</span></div>
      </div>
      <div className="flex flex-col" data-id="element-9701" data-mp="382ebcxbyk41x">
        <span className="text-[12.5px] font-semibold text-[#0D0D0D] mb-1" data-id="element-9702" data-mp="381cwehrtmxex">Information</span>
        {([['Developer', 'App developer'], ['Category', 'Other'], ['Version', '1.0.0']] as [string, string][]).map(([k, v]) => <div key={k} className="flex justify-between border-b border-black/[0.06] py-1.5 text-[11.5px]" data-id="element-9703" data-mp="2ya8htj625ekm"><span className="text-[#8A8A8A]" data-id="element-9704" data-mp="2kemxhparriyj">{k}</span><span className="text-[#0D0D0D]" data-id="element-9705" data-mp="2pc6datn483sl">{v}</span></div>)}
      </div>
    </div>;
}
function ChatGPTFrame({
  step,
  typed,
  installHover
}: {
  step: number;
  typed: string;
  installHover: boolean;
}) {
  return <div className="rounded-2xl border border-sand-200 overflow-hidden bg-white shadow-[0_10px_40px_rgba(43,33,26,0.08)] flex h-[410px] md:h-[470px] text-left" data-id="element-9706" data-mp="2fcnw2kylaw48">
      <ChatGPTSidebar data-id="element-9707" data-mp="dm3zv2f55nsy" />
      <div className="flex-1 relative bg-white overflow-hidden" data-id="element-9708" data-mp="dmm4onfvdach">
        <AnimatePresence mode="wait" data-id="element-9709" data-mp="djq8b3jo6tq5">
          <motion.div key={step} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} className="absolute inset-0 overflow-hidden" data-id="element-9710" data-mp="2xiehyaqyih8j">
            {step === 0 && <ScreenBrowse typed={typed} data-id="element-9711" data-mp="2it8kar7roz2r" />}
            {step === 1 && <ScreenDetail installHover={installHover} data-id="element-9712" data-mp="2ispda5jnnv3i" />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}
function GetStartedFlow() {
  const [screen, setScreen] = useState(0);
  const [typed, setTyped] = useState('');
  const [installHover, setInstallHover] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [pointer, setPointer] = useState({
    x: 50,
    y: 58
  });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const targetRef = useRef('center');

  // Point the cursor at a real element's centre, expressed as a % of the frame
  // container — so it tracks the layout at any container width (and on resize).
  const place = (sel: string) => {
    targetRef.current = sel;
    if (sel === 'center') {
      setPointer({
        x: 50,
        y: 58
      });
      return;
    }
    const c = containerRef.current;
    const el = c?.querySelector<HTMLElement>(`[data-cursor="${sel}"]`);
    if (!c || !el) return;
    const er = el.getBoundingClientRect();
    const cr = c.getBoundingClientRect();
    setPointer({
      x: (er.left + er.width / 2 - cr.left) / cr.width * 100,
      y: (er.top + er.height / 2 - cr.top) / cr.height * 100
    });
  };

  // A looping, choreographed "screen recording": cursor → search box, type Giga,
  // results appear, cursor → Install (which grows on hover).
  useEffect(() => {
    const timers = new Set<ReturnType<typeof setTimeout>>();
    let stopped = false;
    const at = (ms: number, fn: () => void) => {
      timers.add(setTimeout(fn, ms));
    };
    const cycle = () => {
      if (stopped) return;
      timers.clear();
      setScreen(0);
      setTyped('');
      setInstallHover(false);
      setPressed(false);
      place('center');
      at(500, () => place('search'));
      at(1550, () => setPressed(true));
      at(1720, () => setPressed(false));
      at(1760, () => setTyped('G'));
      at(1890, () => setTyped('Gi'));
      at(2020, () => setTyped('Gig'));
      at(2150, () => setTyped('Giga'));
      at(2800, () => setScreen(1)); // → Giga detail
      at(3200, () => place('install'));
      at(3650, () => setInstallHover(true)); // enlarge on hover
      at(4050, () => setPressed(true));
      at(4250, () => setPressed(false));
      at(5400, cycle);
    };
    cycle();
    const onResize = () => place(targetRef.current);
    window.addEventListener('resize', onResize);
    return () => {
      stopped = true;
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', onResize);
    };
  }, []);
  const caption = screen === 1 ? 1 : 0;
  return <div className="w-full max-w-[750px] mx-auto" data-id="element-9713" data-mp="38bjdnrpb35mm">
      <div ref={containerRef} className="relative" data-id="element-9714" data-mp="wohaujd5hqt0">
        <ChatGPTFrame step={screen} typed={typed} installHover={installHover} data-id="element-9715" data-mp="wmwf4qf3o1tk" />
        {/* Animated cursor */}
        <div className="absolute z-20 pointer-events-none transition-[left,top] duration-[850ms] ease-[cubic-bezier(0.33,0,0.2,1)]" style={{
        left: `${pointer.x}%`,
        top: `${pointer.y}%`
      }} data-id="element-9716" data-mp="slknyggkv8j9">
          <svg width="23" height="23" viewBox="0 0 24 24" className={['block transition-transform duration-150', pressed ? 'scale-90' : 'scale-100'].join(' ')} style={{
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))'
        }} data-id="element-9717" data-mp="1gcydyfr9as12">
            <path d="M5.5 3.2 L5.5 17.6 L9.2 14.1 L11.7 19.7 L14 18.7 L11.5 13.2 L16.6 13.2 Z" fill="#fff" stroke="#1A1A1A" strokeWidth="1.1" strokeLinejoin="round" data-id="element-9718" data-mp="1pcd4ff7fb2lk" />
          </svg>
        </div>
        {/* Caption overlaid on the frame — bottom centre, dark pill */}
        <div className="absolute inset-x-0 bottom-4 flex justify-center px-4 pointer-events-none z-30" data-id="element-9719" data-mp="3m66kybizbhby">
          <AnimatePresence mode="wait" data-id="element-9720" data-mp="1q3bicg56h261">
            <motion.span key={caption} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.3
          }} className="inline-flex items-center gap-2.5 bg-ink text-white rounded-full pl-2 pr-4 py-1.5 text-[13px] md:text-[14px] font-medium shadow-[0_6px_20px_rgba(0,0,0,0.25)]" data-id="element-9721" data-mp="1q4cwithhq4rz">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand text-white text-[12px] font-bold flex-none" data-id="element-9722" data-mp="2e07fs8y5nzx5">{caption + 1}</span>
              {CAPTIONS[caption]}
            </motion.span>
          </AnimatePresence>
        </div>
      </div>
    </div>;
}

// ── "More than answering questions" — one ad campaign carried from analyze
// → decide → coordinate → execute. Tabs advance as the section scrolls, and
// each answer is rendered the way ChatGPT actually shows it: plain text,
// bullets and a simple table, led by the "working" indicator.
const CHAIN = [{
  verb: 'Analyze',
  icon: BarChart3,
  prompt: 'hey! can you analyze our Q3 ad campaign performance?',
  kind: 'analyze',
  leadIn: "I'll pull the last 7 days across your four ad sets, then flag what's dragging the account down.",
  statuses: ['Analyzing Meta Ads Performance', 'Reviewing the last 7 days']
}, {
  verb: 'Decide',
  icon: Lightbulb,
  prompt: 'which ad sets should we cut, and where should the budget go?',
  kind: 'decide',
  leadIn: 'Let me weigh each ad set against your ROAS and CPA targets and give you a clear call.',
  statuses: ['Comparing to Your Targets', 'Weighing the options']
}, {
  verb: 'Coordinate',
  icon: Users,
  prompt: 'can you let the team know in Slack?',
  kind: 'coordinate',
  leadIn: "I'll drop a clear summary in #growth so nobody's caught off guard.",
  statuses: ['Posting to #growth', 'Notifying the team']
}, {
  verb: 'Execute',
  icon: Zap,
  prompt: 'go ahead — pause 2 & 3 and scale up 1 & 4',
  kind: 'execute',
  leadIn: "On it — I'll make the changes directly in Meta Ads Manager.",
  statuses: ['Updating Meta Ads Manager', 'Applying changes']
}];
function BeatContent({
  b,
  working
}: {
  b: (typeof CHAIN)[number];
  working: boolean;
}) {
  const rows = [{
    name: 'Ad set 1 · Broad',
    roas: '4.2×',
    cpa: '$18',
    good: true
  }, {
    name: 'Ad set 2 · Lookalike',
    roas: '1.1×',
    cpa: '$72',
    good: false
  }, {
    name: 'Ad set 3 · Interests',
    roas: '0.9×',
    cpa: '$88',
    good: false
  }, {
    name: 'Ad set 4 · Retargeting',
    roas: '3.8×',
    cpa: '$21',
    good: true
  }];
  return <div className="flex flex-col gap-3.5 text-[15px] leading-[1.6] text-ink" data-id="element-9723" data-mp="1h9f0y6q8uxqm">
      {/* User prompt — right-aligned grey bubble */}
      <div className="self-end max-w-[90%] bg-[#F4F4F4] rounded-[24px] px-5 py-3 text-[#0D0D0D]" data-id="element-9724" data-mp="24xy9mnd7hcj8">
        <span className="inline-flex items-center gap-1.5 mr-2 align-[-5px]" data-id="element-9725" data-mp="29qredb8ockvb">
          <img src={GIGA_MARK} alt="" className="w-[22px] h-[22px] rounded-[6px]" data-id="element-9726" data-mp="2j3zwilgjgdpb" />
          <strong className="font-semibold" data-id="element-9727" data-mp="2f2whg16belp5">Giga</strong>
        </span>
        {b.prompt}
      </div>
      <span className="self-end -mt-1.5 pr-0.5 text-ink-faint" data-id="element-9728" data-mp="2suba6zq0y19e"><Copy className="w-[18px] h-[18px]" data-id="element-9729" data-mp="2sttatu82f6x9" /></span>
      {/* Assistant answer — left-aligned */}
      <p data-id="element-9730" data-mp="2nvron0fcerhk">{b.leadIn}</p>
      {working ? <GigaWorking statuses={b.statuses} bare data-id="element-9731" data-mp="2nxkp1xzd4fh4" /> : <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.25
    }} className="flex flex-col gap-3.5" data-id="element-9732" data-mp="206ox90uwpk9v">
            {b.kind === 'analyze' && <>
              <div className="overflow-hidden rounded-md border border-sand-200 text-[13.5px]" data-id="element-9733" data-mp="2a4mlhocrnh2h">
                <div className="grid grid-cols-[1.7fr_0.7fr_0.7fr] bg-cream border-b border-sand-200 text-[11px] font-semibold text-ink-muted" data-id="element-9734" data-mp="253yxzcnqxid0">
                  <span className="px-2.5 py-1.5" data-id="element-9735" data-mp="3gjvy7x11bopj">Ad set</span><span className="px-2.5 py-1.5 text-right" data-id="element-9736" data-mp="3giubuwekjfsi">ROAS</span><span className="px-2.5 py-1.5 text-right" data-id="element-9737" data-mp="3oklgg11sm6is">CPA</span>
                </div>
                {rows.map(r => <div key={r.name} className="grid grid-cols-[1.7fr_0.7fr_0.7fr] border-b border-sand-100 last:border-0" data-id="element-9738" data-mp="3sm6rrhyoicqv">
                    <span className="px-2.5 py-1.5 text-ink truncate" data-id="element-9739" data-mp="1fm1i0gg78uw">{r.name}</span>
                    <span className={['px-2.5 py-1.5 text-right', r.good ? 'text-ink' : 'text-red-600 font-semibold'].join(' ')} data-id="element-9740" data-mp="6eqe712kzopz">{r.roas}</span>
                    <span className="px-2.5 py-1.5 text-right text-ink-muted" data-id="element-9741" data-mp="34y710i1damca">{r.cpa}</span>
                  </div>)}
              </div>
              <p data-id="element-9742" data-mp="39tcmxgurs10d">Ad sets 2 and 3 are well under your 2× ROAS target — worth a closer look.</p>
            </>}
            {b.kind === 'decide' && <ul className="list-disc pl-5 flex flex-col gap-1" data-id="element-9743" data-mp="3jls1032y45fz">
                <li data-id="element-9744" data-mp="2m64ewr5yidun">Pause <strong data-id="element-9745" data-mp="2m6e5ogytsp1e">ad set 2</strong> — 1.1× ROAS, $72 CPA</li>
                <li data-id="element-9746" data-mp="2m5upp5sqkaq0">Pause <strong data-id="element-9747" data-mp="2m40a8708hatb">ad set 3</strong> — 0.9× ROAS, $88 CPA</li>
                <li data-id="element-9748" data-mp="2r3wfsaokxaaz">Reallocate that budget to ad sets 1 &amp; 4, your top performers</li>
              </ul>}
            {b.kind === 'coordinate' && <blockquote className="border-l-2 border-sand-300 pl-3 text-ink-muted" data-id="element-9749" data-mp="2jjc5cpe0ihyi">Heads up team — pausing ad sets 2 &amp; 3 (ROAS under 1×) and moving budget to 1 &amp; 4. Full breakdown in the thread.</blockquote>}
            {b.kind === 'execute' && <ul className="list-disc pl-5 flex flex-col gap-1" data-id="element-9750" data-mp="24p77nz4jsrj7">
                <li data-id="element-9751" data-mp="20mu3hf5g91oj">Paused ad set 2</li>
                <li data-id="element-9752" data-mp="20nvrek7kdino">Paused ad set 3</li>
                <li data-id="element-9753" data-mp="20ox5yynsgna5">Increased ad set 1 budget by 40%</li>
                <li data-id="element-9754" data-mp="1vv2n1xg0ccc4">Increased ad set 4 budget by 40%</li>
              </ul>}
          </motion.div>}
    </div>;
}
export function MoreThanAnswers({
  embedded = false
}: {
  embedded?: boolean;
} = {}) {
  const [active, setActive] = useState(0);
  const [working, setWorking] = useState(true);
  const [nonce, setNonce] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);
  const inView = useInView(ref, {
    amount: 0.4
  });
  // Jump to a tab from a click, bumping `nonce` so the auto-advance + working
  // timers restart in full — even when re-clicking the tab that's showing.
  const goTo = (i: number) => {
    setActive(i);
    setNonce(n => n + 1);
  };
  // Start from the first tab whenever the section scrolls into view.
  useEffect(() => {
    if (inView) setActive(0);
  }, [inView]);
  // Auto-advance every 3s while in view; changing tab or clicking restarts it.
  useEffect(() => {
    if (!inView) return;
    const id = setTimeout(() => setActive(a => (a + 1) % CHAIN.length), 3000);
    return () => clearTimeout(id);
  }, [active, inView, nonce]);
  // The "working" indicator plays briefly on each tab, then settles into the
  // finished answer.
  useEffect(() => {
    setWorking(true);
    const id = setTimeout(() => setWorking(false), 1600);
    return () => clearTimeout(id);
  }, [active, nonce]);
  const b = CHAIN[active];
  return <div ref={ref} className={['flex flex-col items-center', embedded ? 'gap-0' : 'gap-6 md:gap-8'].join(' ')} data-id="element-9755" data-mp="2365uo27vykvm">
      {!embedded && <div className="flex flex-col items-center text-center gap-4" data-id="element-9756" data-mp="1kh3lx1zohrnp">
          <h2 className="font-title font-extrabold text-4xl md:text-[54px] leading-[1.02] tracking-tight max-w-[18ch]" data-id="element-9757" data-mp="1fj20wbiozi9l">More than just answering questions</h2>
          <p className="text-base md:text-lg leading-relaxed text-ink-muted max-w-[52ch]" data-id="element-9758" data-mp="1tk4m6m1v4ojg">Most AI stops at an answer. Giga takes action and gets the work done.</p>
          <div className="inline-flex items-center gap-1 bg-cream border border-sand-200 rounded-full p-1 mt-1" data-id="element-9759" data-mp="23fxzw8rbio2t">
            {CHAIN.map(({
          verb,
          icon: Icon
        }, i) => <button key={verb} type="button" onClick={() => goTo(i)} className={['inline-flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-full text-[13px] md:text-[15px] font-semibold transition-colors duration-300', i === active ? 'bg-ink text-white' : 'text-ink-muted hover:text-ink'].join(' ')} data-id="element-9760" data-mp="2c8t6b5siupu6" data-mpi={i}><Icon className="w-4 h-4" data-id="element-9761" data-mp="2h85upuz1k4yv" />{verb}</button>)}
          </div>
        </div>}
      <div className={['w-full bg-white border border-sand-200 rounded-2xl p-5 md:p-7 shadow-[0_6px_28px_rgba(43,33,26,0.06)]', embedded ? 'max-w-none min-h-[430px]' : 'max-w-[640px] min-h-[400px]'].join(' ')} data-id="element-9762" data-mp="2h6b6a6ok9uq1">
        <AnimatePresence mode="wait" data-id="element-9763" data-mp="1o9dwjqnvs7g0">
          <motion.div key={active} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.25
        }} data-id="element-9764" data-mp="1oafkuwtwqqfo">
            <BeatContent b={b} working={working} data-id="element-9765" data-mp="1t8h3jgnf8nqm" />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}

// ── "Put it down anywhere" — Giga is its own system, reachable from any
// provider, so nothing is locked in.
const HANDOFF_TARGETS = ['Slack', 'Claude Code', 'your other tools'];
const PROVIDERS = [{
  name: 'ChatGPT',
  logo: OPENAI_ICON
}, {
  name: 'Slack',
  logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg'
}, {
  name: 'Claude',
  logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/anthropic.svg'
}];
const SYSTEM_BOXES = [{
  icon: Users,
  name: 'Team',
  desc: 'Users, groups and spaces.'
}, {
  icon: Folder,
  name: 'Projects',
  desc: 'Ongoing work, kept together.'
}, {
  icon: Plug,
  name: 'Connectors',
  desc: 'All your tools, connected once.'
}, {
  icon: Sparkles,
  name: 'Skills',
  desc: 'Reusable know-how, applied on demand.'
}, {
  icon: FileText,
  name: 'Files',
  desc: 'Docs, decks and exports, understood.'
}, {
  icon: Database,
  name: 'Memories',
  desc: 'Shared, inspectable company knowledge.'
}];

// The tool name at the end of the headline, rotating on a loop.
function RotatingWord() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI(x => (x + 1) % HANDOFF_TARGETS.length), 2200);
    return () => clearInterval(id);
  }, []);
  return <span className="text-brand whitespace-nowrap" data-id="element-9766" data-mp="3m388j9js4usz">
      <AnimatePresence mode="wait" data-id="element-9767" data-mp="3q5wkhq3sihu6">
        <motion.span key={i} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.28,
        ease: [0.22, 1, 0.36, 1]
      }} className="inline-block" data-id="element-9768" data-mp="3q64vtdk9lf39">{HANDOFF_TARGETS[i]}</motion.span>
      </AnimatePresence>
    </span>;
}
/**
 * The recipe every panel on this page shares.
 *
 * Was `rounded-[28px]` with no shadow — a 28px radius reads as a soft app tile
 * rather than the 22px card the rest of the site uses, and a white card with
 * no shadow on a cream ground has nothing but a hairline holding it off the
 * page. The two-part shadow (1px contact edge + a wide soft cast) is the same
 * one the home page board uses.
 */
const CARD = 'flex flex-col rounded-[22px] border border-sand-200 bg-white p-6 md:p-12 shadow-[0_1px_2px_rgba(43,33,26,0.04),0_24px_60px_-40px_rgba(43,33,26,0.35)]';
export function GigaForChatGPT2() {
  // Real integrations from the Giga catalog (same source as the /integrations
  // page). Seed from the prerender prime cache when present, else fetch on
  // mount. Purely display filler for the right columns — no filtering wired up.
  const primed = getCachedIntegrations();
  const [integrations, setIntegrations] = useState<IntegrationListItem[]>(primed ?? []);
  useEffect(() => {
    if (primed && primed.length) return;
    let cancelled = false;
    fetchIntegrations().then(rows => {
      if (!cancelled) setIntegrations(rows);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // No popularity field on the catalog, so curate: known tools to the top (in
  // POPULAR_SLUGS order), the rest keep the API's alphabetical order behind them.
  const ranked = [...integrations].sort((a, b) => (POPULAR_RANK.get(a.slug) ?? Infinity) - (POPULAR_RANK.get(b.slug) ?? Infinity));
  const shown = ranked.slice(0, 60);
  return <div className="w-full bg-cream text-ink font-body" data-id="element-9769" data-mp="3emddnw85en9s">
      <div className="max-w-[1120px] mx-auto px-6 md:px-10 pt-28 md:pt-36 pb-20 md:pb-28 flex flex-col gap-10 md:gap-14" data-id="element-9770" data-mp="30ylea67l4l5f">

        {/* ── Hero ──────────────────────────────────────────────── */}
        <section className="flex flex-col items-center text-center gap-5" data-id="element-9771" data-mp="3apg23f0hbssz">
          <div className="flex items-center gap-2.5" data-id="element-9772" data-mp="35ptqtb47ommt">
            <img src={OPENAI_ICON} alt="ChatGPT" className="w-5 h-5" data-id="element-9773" data-mp="35r4sn5e3jkba" />
            <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-ink-faint" data-id="element-9774" data-mp="2hzppxbier0zi">Giga inside ChatGPT</span>
          </div>
          <h1 className="font-title font-extrabold text-4xl md:text-[56px] leading-[1.03] tracking-tight max-w-[18ch]" data-id="element-9775" data-mp="2e08hc97opnq4">
            The marketing operating system inside ChatGPT.
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty max-w-[58ch]" data-id="element-9776" data-mp="2mxr5vnkjek6r">
            Every marketing tool your team touches — connected to the chat you already have open.
          </p>
        </section>

        {/* ── Connect all your tools ────────────────────────────── */}
        <section className={`${CARD} gap-10 md:gap-12`} data-id="element-9777" data-mp="1k56mae81r02w">
          <div className="flex flex-col gap-3 max-w-[60ch]" data-id="element-9778" data-mp="1p6cfed5mcpcy">
            <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-brand-dark" data-id="element-9779" data-mp="1p3gf4i6atolc">Integrations</span>
            <h2 className="font-title font-extrabold text-3xl md:text-[42px] leading-[1.05] tracking-tight" data-id="element-9780" data-mp="1g54ucws45l0t">
              Connect <em className="italic text-brand underline decoration-2 underline-offset-[6px]" data-id="element-9781" data-mp="23u78vvrgaxr3">all</em> your tools, not just MCP.
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-9782" data-mp="27vj025pz3r1e">
              ChatGPT connects to the handful of tools that speak MCP. Giga connects to{' '}
              <strong className="text-brand font-semibold" data-id="element-9783" data-mp="1z2pg3uzl03h7">literally everything else</strong>.
            </p>
          </div>

          {/* A · Segmented coverage meters */}
          <div className="rounded-2xl border border-sand-200 bg-cream p-6 md:p-8 flex flex-col gap-7" data-id="element-9784" data-mp="2rkjo7rp0zwrp">
            {METERS.map((group, gi) => <div key={group.metric} className={['flex flex-col gap-4', gi > 0 ? 'pt-7 border-t border-sand-200' : ''].join(' ')} data-id="element-9785" data-mp="31fryawqj335i" data-mpi={gi}>
                <span className="font-mono text-[11px] tracking-[0.12em] uppercase text-ink-faint" data-id="element-9786" data-mp="2wfcogf5ggdsa">{group.metric}</span>
                <div className="flex flex-col gap-3.5" data-id="element-9787" data-mp="2mj9iszbe9x0l">
                  {group.rows.map(r => <MeterRow key={r.who} who={r.who} score={r.score} brand={r.brand} data-id="element-9788" data-mp="2mldkrbpqgly8" />)}
                </div>
              </div>)}
          </div>

          {/* B · Ontology — categories on the left, real integrations right */}
          <div className="flex flex-col gap-4" data-id="element-9789" data-mp="28r34lirsy03s">
            <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-ink-faint" data-id="element-9790" data-mp="28rw542063tdh">Marketing tools by category</span>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-9791" data-mp="1ytf92qmdgokk">

              {/* Left · category filters (fillers — not wired to the grid) */}
              <div className="lg:col-span-1 flex flex-col gap-2 lg:max-h-[600px] lg:overflow-y-auto lg:pr-2" data-id="element-9792" data-mp="1usd9u9sggcil">
                <span className="inline-flex items-center gap-2 rounded-lg border border-brand bg-white py-2 px-3 text-[13px] font-semibold text-brand" data-id="element-9793" data-mp="a9j8czdedw2c">
                  <Filter className="w-4 h-4" data-id="element-9794" data-mp="ja7lzsyvpuep" />All Categories
                </span>
                {MARKETING_CATEGORIES.map(({
                name,
                icon: Icon
              }) => <span key={name} className="inline-flex items-center gap-2 rounded-lg border border-sand-200 bg-white py-2 px-3 text-[13px] font-medium text-ink hover:border-sand-300 transition-colors cursor-default" data-id="element-9795" data-mp="3mrhe80r59i08">
                    <Icon className="w-4 h-4 text-ink-faint flex-none" data-id="element-9796" data-mp="jpu1qlvf2ue" />{name}
                  </span>)}
              </div>

              {/* Right · real integrations from the catalog (3 columns) */}
              <div className="lg:col-span-3" data-id="element-9797" data-mp="341httr690r35">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 lg:max-h-[600px] lg:overflow-y-auto lg:pr-1" data-id="element-9798" data-mp="33ylupzinzwd4">
                  {shown.length === 0 ? Array.from({
                  length: 12
                }).map((_, i) => <div key={i} className="h-[58px] rounded-xl bg-cream border border-sand-200 animate-pulse" data-id="element-9799" data-mp="22ebj18juxiwl" data-mpi={i} />) : shown.map(item => <IntegrationTile key={item.slug} item={item} available={availableInChatGPT(item)} data-id="element-9800" data-mp="2bcx1z4t8qzxw" />)}
                </div>
              </div>

            </div>
          </div>

        </section>

        {/* ── Never hit a dead end — plain background ────────────── */}
        <section className="flex flex-col gap-8 md:gap-12 py-4 md:py-10" data-id="element-9801" data-mp="2l98cpqd6nhq8">
          <div className="flex flex-col items-center text-center gap-3 max-w-[58ch] mx-auto" data-id="element-9802" data-mp="2gbqfuycfwsyl">
            <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-brand-dark" data-id="element-9803" data-mp="2u2d4tysef4hh">Add anything</span>
            <h2 className="font-title font-extrabold text-3xl md:text-[42px] leading-[1.05] tracking-tight" data-id="element-9804" data-mp="2q2usvsik23qs">Never hit a dead end.</h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-9805" data-mp="2z2r8z5qvpua8">
              No integration yet? If a tool isn't in Giga's directory, it finds it on the web and
              builds a connection on the spot.
            </p>
          </div>

          {/* Ask → Giga runs the checks & builds it → connect link */}
          <div className="flex flex-col items-center w-full" data-id="element-9806" data-mp="38pk4v9gcii1l">
            {/* The ask */}
            <div className="w-full max-w-[700px]" data-id="element-9807" data-mp="3doeyl6fupr0h">
              <ChatBox send data-id="element-9808" data-mp="3dr9k71alphvh">
                <span className="inline-flex items-center gap-1.5 font-semibold text-brand-dark align-[-4px] mr-1.5" data-id="element-9809" data-mp="2zxi6dgpu7bty">
                  <img src={GIGA_MARK} alt="" className="w-[18px] h-[18px] rounded-[4px]" data-id="element-9810" data-mp="34ptgtdi7eg0r" />@Giga
                </span>
                can we connect Larkspur?
              </ChatBox>
            </div>

            <FlowLink data-id="element-9811" data-mp="2v004ube5fyvz" />

            {/* Giga runs the checks and builds it */}
            <div className="w-full max-w-[560px] relative rounded-2xl border-2 border-dashed border-[#D6CCC0] bg-sand-50 pt-7 px-4 md:px-5 pb-4 flex flex-col gap-2" data-id="element-9812" data-mp="2gynv4hpl0tij">
              <div className="absolute -top-3 left-5 flex items-center gap-2 bg-ink text-white rounded-full py-1.5 pl-3 pr-4" data-id="element-9813" data-mp="2l2lr4jjm1o7d">
                <Sparkles className="w-3.5 h-3.5 text-brand-lighter" data-id="element-9814" data-mp="1cxb2n7sx2xf3" />
                <span className="font-mono text-[11px] font-semibold tracking-[0.14em] uppercase" data-id="element-9815" data-mp="1cznqg1lcw202">Giga builds it</span>
              </div>
              {DEADEND_CHECKS.map(({
              icon: Icon,
              label,
              status,
              tone
            }) => <div key={label} className="bg-white border border-sand-200 rounded-xl p-3 flex items-center gap-2.5" data-id="element-9816" data-mp="1qx2kxkqguzyw">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-brand/10 text-brand-dark flex-none" data-id="element-9817" data-mp="1vulz6kn5zcqu"><Icon className="w-4 h-4" data-id="element-9818" data-mp="25miawyi6b1dj" /></span>
                  <span className="text-[13px] text-ink flex-1 min-w-0" data-id="element-9819" data-mp="25l6y696r2jaa">{label}</span>
                  {tone === 'green' ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 flex-none" data-id="element-9820" data-mp="2an60dfqr7a5n"><Check className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-9821" data-mp="2enpv6cnuu1om" />{status}</span> : <span className="text-[11px] text-ink-faint flex-none" data-id="element-9822" data-mp="3036ahf3u6ym">{status}</span>}
                </div>)}
            </div>

            <FlowLink data-id="element-9823" data-mp="3v22gryzoaozd" />

            {/* Giga's reply with the connect link */}
            <div className="w-full max-w-[700px]" data-id="element-9824" data-mp="3q5lqoz8wy0jn">
              <ChatBox data-id="element-9825" data-mp="3q6dn2cac42az">
                <div className="flex items-start gap-2.5" data-id="element-9826" data-mp="3l5zemt4tmqcf">
                  <img src={GIGA_MARK} alt="" className="w-[18px] h-[18px] rounded-[4px] flex-none mt-0.5" data-id="element-9827" data-mp="3l7kbipelyldg" />
                  <div className="flex flex-col gap-2.5" data-id="element-9828" data-mp="mpn0omug3cn4">
                    <span data-id="element-9829" data-mp="hq21cbbd529v">Done — I've built the <strong className="font-semibold" data-id="element-9830" data-mp="hqtyvroita2s">Larkspur</strong> integration. Click to connect it:</span>
                    <a href="#" className="self-start inline-flex items-center gap-1.5 bg-brand hover:bg-brand-dark text-white rounded-full px-4 py-2 text-[13px] font-bold transition-colors" data-id="element-9831" data-mp="cvo566ovmr45">
                      Connect Larkspur <ArrowRight className="w-3.5 h-3.5" data-id="element-9832" data-mp="10nlftuglbd3o" />
                    </a>
                  </div>
                </div>
              </ChatBox>
            </div>
          </div>

        </section>

        {/* ── Secure vault — its own panel ───────────────────────── */}
        <section className={`${CARD} gap-8 md:gap-10`} data-id="element-9833" data-mp="2yz52j73cqufz">
          <div className="flex flex-col items-center text-center gap-3 max-w-[58ch] mx-auto" data-id="element-9834" data-mp="2l4ku1jwwlpco">
            <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-brand-dark" data-id="element-9835" data-mp="2q3xexz47smcm">Secure vault</span>
            <h2 className="font-title font-extrabold text-3xl md:text-[42px] leading-[1.05] tracking-tight" data-id="element-9836" data-mp="2b6olmzvcp8i9">Keep private data out of the conversation.</h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-9837" data-mp="226rtgo9r5rig">
              API keys, OAuth tokens, and database credentials stay securely in Giga's encrypted
              vault. ChatGPT gets the access it needs—without ever seeing your secrets.
            </p>
          </div>

          {/* Prompt → vault → answer, one centred column */}
          <div className="flex flex-col items-center w-full" data-id="element-9838" data-mp="5cf5x6a348st">
            <div className="w-full max-w-[700px]" data-id="element-9839" data-mp="fjhptpb8xna3">
              <ChatBox send data-id="element-9840" data-mp="fg280eq6u04z">
                <span className="inline-flex items-center gap-1.5 font-semibold text-brand-dark align-[-4px] mr-1.5" data-id="element-9841" data-mp="ajvb1bfrryz6">
                  <img src={GIGA_MARK} alt="" className="w-[18px] h-[18px] rounded-[4px]" data-id="element-9842" data-mp="obrzn5i8bqqt" />@Giga
                </span>
                what were our metrics last week?
              </ChatBox>
            </div>

            <FlowLink data-id="element-9843" data-mp="y7mssuk2x47u" />

            <div className="w-full max-w-[560px] flex flex-col gap-2.5" data-id="element-9844" data-mp="yb0rnstjavaj">
              <div className="relative rounded-2xl border-2 border-dashed border-[#D6CCC0] bg-sand-50 pt-7 px-4 md:px-5 pb-4 flex flex-col gap-2" data-id="element-9845" data-mp="t91r0qp6icha">
                <div className="absolute -top-3 left-5 flex items-center gap-2 bg-ink text-white rounded-full py-1.5 pl-3 pr-4" data-id="element-9846" data-mp="1380wk0ends02">
                  <ShieldCheck className="w-3.5 h-3.5 text-brand-lighter" data-id="element-9847" data-mp="3907hyxrqacez" />
                  <span className="font-mono text-[11px] font-semibold tracking-[0.14em] uppercase" data-id="element-9848" data-mp="34znivonlgyq7">Giga Vault</span>
                </div>
                <div className="bg-white border border-sand-200 rounded-xl p-3 flex items-center gap-x-2.5 gap-y-1.5 flex-wrap" data-id="element-9849" data-mp="36frkwtmrvyoi">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-brand/10 text-brand-dark flex-none" data-id="element-9850" data-mp="3bhqnw2elotkx"><Database className="w-4 h-4" data-id="element-9851" data-mp="3kgc2xnkv5qgy" /></span>
                  <div className="flex flex-col min-w-0" data-id="element-9852" data-mp="3kf0x7i7uy8n7">
                    <span className="text-[12.5px] font-semibold text-ink leading-tight" data-id="element-9853" data-mp="2nqbejpoepc06">Database credentials</span>
                    <span className="font-mono text-[11px] text-ink-faint tracking-wider truncate" data-id="element-9854" data-mp="2rqvgr7nu75t6">postgres://••••••••</span>
                  </div>
                  <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-brand-dark" data-id="element-9855" data-mp="2won73x8u2jv9"><ShieldCheck className="w-3.5 h-3.5" data-id="element-9856" data-mp="31jjblwv9azp9" />Secured by Giga</span>
                </div>
                <div className="bg-white border border-sand-200 rounded-xl p-3 flex items-center gap-x-2.5 gap-y-1.5 flex-wrap" data-id="element-9857" data-mp="23zzpqcr7gemz">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-brand/10 text-brand-dark flex-none" data-id="element-9858" data-mp="21he5ezndampi"><KeyRound className="w-4 h-4" data-id="element-9859" data-mp="1wi9xsdg3cx1a" /></span>
                  <div className="flex flex-col min-w-0" data-id="element-9860" data-mp="1wggt33p1gmvx">
                    <span className="text-[12.5px] font-semibold text-ink leading-tight" data-id="element-9861" data-mp="1rif3s110rrc0">API token</span>
                    <span className="font-mono text-[11px] text-ink-faint tracking-wider truncate" data-id="element-9862" data-mp="1nh3fbytvtc5e">Bearer sk_live_••••</span>
                  </div>
                  <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-brand-dark" data-id="element-9863" data-mp="1ds21ae6wd4u2"><ShieldCheck className="w-3.5 h-3.5" data-id="element-9864" data-mp="18sytilzj8kie" />Secured by Giga</span>
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 text-[12px] text-ink-muted" data-id="element-9865" data-mp="13ue3tghwpj87"><Check className="w-3.5 h-3.5 text-emerald-600 flex-none" strokeWidth={2.5} data-id="element-9866" data-mp="zsaqembog4ka" /><span data-id="element-9867" data-mp="zrhjnlcpxf25"><strong className="text-ink font-medium" data-id="element-9868" data-mp="zudciv9pldjq">MCP &amp; OAuth tools</strong> skip the vault — already secure.</span></span>
            </div>

            <FlowLink data-id="element-9869" data-mp="l5bzcsgqfc63" />

            <div className="w-full max-w-[700px]" data-id="element-9870" data-mp="l40mm35b6u2u">
              <ChatBox data-id="element-9871" data-mp="p6xbucpctofr">
                <span className="inline-flex items-center gap-2" data-id="element-9872" data-mp="p88cw4rf0i36">
                  <img src={GIGA_MARK} alt="" className="w-[18px] h-[18px] rounded-[4px] flex-none" data-id="element-9873" data-mp="b7e5npg3sn63" />
                  <span data-id="element-9874" data-mp="g68ub80r55y4">Last week: <strong className="font-semibold" data-id="element-9875" data-mp="g5z69pz9p8v2">1,240 signups · $48k revenue · 3.4× ROAS</strong>.</span>
                </span>
              </ChatBox>
            </div>
          </div>
        </section>

        {/* ── More than answering questions — analyze→execute, tabbed on scroll ── */}
        <section className="py-2 md:py-8" data-id="element-9876" data-mp="u6s008hqvzx4">
          <MoreThanAnswers data-id="element-9877" data-mp="u5qmzyh653cr" />
        </section>

        {/* ── Embedded expertise ────────────────────────────────── */}
        <section className={`${CARD} gap-8 md:gap-10`} data-id="element-9878" data-mp="z3a559gzm09z">
          <div className="flex flex-col items-center text-center gap-3 max-w-[56ch] mx-auto" data-id="element-9879" data-mp="1mtw84mc4wud4">
            <h2 className="font-title font-extrabold text-3xl md:text-[42px] leading-[1.05] tracking-tight" data-id="element-9880" data-mp="1rmpefen94arb">Embedded expertise.</h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-9881" data-mp="3ca8d37mfwxtz">
              Giga comes pre-packed with the full landscape of approaches, strategies, skills, and
              tools — a whole library you can put to work.
            </p>
          </div>

          {/* Two ChatGPT windows: plain question vs @Giga */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 items-start" data-id="element-9882" data-mp="2tcoyloh7qn06">
            {/* ── ChatGPT ── */}
            <div className="flex flex-col gap-3" data-id="element-9883" data-mp="2of5kcokima88">
              <div className="flex items-center gap-2" data-id="element-9884" data-mp="2oggq2txits1z">
                <img src={OPENAI_ICON} alt="" className="w-4 h-4" data-id="element-9885" data-mp="32eofmgs89r34" />
                <span className="font-mono text-[11px] tracking-[0.12em] uppercase text-ink-faint" data-id="element-9886" data-mp="2xeaku0fn0dn1">ChatGPT</span>
              </div>
              <div className="bg-white border border-sand-200 rounded-[22px] shadow-[0_4px_20px_rgba(43,33,26,0.06)] py-3 pl-4 pr-2.5 flex items-center gap-2.5" data-id="element-9887" data-mp="hmcue7gddvts">
                <span className="flex-1 text-[14.5px] text-ink" data-id="element-9888" data-mp="wr7lerqqz2zu">What is UGC marketing?</span>
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0D0D0D] text-white flex-none" data-id="element-9889" data-mp="11pitoy1e95fy"><ArrowUp className="w-3.5 h-3.5" data-id="element-9890" data-mp="15qubihe10mdl" /></span>
              </div>
              <div className="bg-white border border-sand-200 rounded-2xl p-5 flex flex-col gap-3 text-[14px] leading-[1.6] text-ink" data-id="element-9891" data-mp="1ai2mviyx2hte">
                <p data-id="element-9892" data-mp="1fg44s0klwb3a"><strong className="font-semibold" data-id="element-9893" data-mp="1fg45y3wceh4v">User-generated content (UGC) marketing</strong> is a strategy where brands use content made by their customers or unpaid creators — reviews, photos, videos, and social posts — instead of producing it themselves.</p>
                <p data-id="element-9894" data-mp="1tgonyxufo8xb">It's generally seen as more authentic and trustworthy than brand-made content, and it's widely used across e-commerce, social media, and paid advertising.</p>
                <p className="text-ink-muted" data-id="element-9895" data-mp="3r8sv86y8pysj">Would you like some examples or best practices to get started?</p>
              </div>
            </div>

            {/* ── Giga ── */}
            <div className="flex flex-col gap-3" data-id="element-9896" data-mp="3nq3kjlpyhbrq">
              <div className="flex items-center gap-2" data-id="element-9897" data-mp="3ixk84s308moi">
                <img src={GIGA_MARK} alt="" className="w-4 h-4 rounded-[3px]" data-id="element-9898" data-mp="3ivzcez4yexp2" />
                <span className="font-mono text-[11px] tracking-[0.12em] uppercase text-brand-dark" data-id="element-9899" data-mp="3dzioo61o2lci">Giga in ChatGPT</span>
              </div>
              <div className="bg-white border border-sand-200 rounded-[22px] shadow-[0_4px_20px_rgba(43,33,26,0.06)] py-3 pl-4 pr-2.5 flex items-center gap-2.5" data-id="element-9900" data-mp="aia8dqml7tjw">
                <span className="flex-1 text-[14.5px] text-ink" data-id="element-9901" data-mp="5jp7rq3fb7ux"><span className="font-semibold text-brand-dark" data-id="element-9902" data-mp="1hc55ajz3q2d">@Giga</span> what is UGC marketing?</span>
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0D0D0D] text-white flex-none" data-id="element-9903" data-mp="3soetlu8ffi8w"><ArrowUp className="w-3.5 h-3.5" data-id="element-9904" data-mp="2m8340hjo78ox" /></span>
              </div>
              <div className="bg-white border border-brand/30 ring-1 ring-brand/10 rounded-2xl p-5 flex flex-col gap-3 text-[14px] leading-[1.6] text-ink" data-id="element-9905" data-mp="2h8zyyx3ox2gy">
                <GigaWorking data-id="element-9906" data-mp="1hljf9hl3aw7t" />
                <p data-id="element-9907" data-mp="1ctjke3jvmti3">UGC is content made by your <strong className="font-semibold" data-id="element-9908" data-mp="1ct9ro87g688p">customers and creators</strong> — not your brand. It works best for DTC, apps, and social-first teams.</p>
                <p data-id="element-9909" data-mp="18pk7duxt2cth">Here's everything you'd need to run it:</p>
                <p data-id="element-9910" data-mp="13u4tx43pmixl">📖 <a href="#" className="text-brand-dark font-semibold underline underline-offset-2 hover:text-brand" data-id="element-9911" data-mp="13uen10k1x67i">The Giga Guide to UGC Marketing</a> — the canonical playbook (12 min read).</p>
                <p data-id="element-9912" data-mp="1witzbfbstaf5"><strong className="font-semibold" data-id="element-9913" data-mp="1witwl7keywbg">42 tools</strong> across 6 subcategories — creator sourcing, editing &amp; captions, rights &amp; contracts, whitelisting, hooks &amp; scripts, and analytics. Want me to start with one?</p>
                <div className="flex flex-col gap-1.5" data-id="element-9914" data-mp="1mlpdxhps1ize">
                  <p data-id="element-9915" data-mp="g425qz2x1x49"><strong className="font-semibold" data-id="element-9916" data-mp="g4u2wec5w2wn">Skills you can install:</strong></p>
                  <ul className="flex flex-col gap-1" data-id="element-9917" data-mp="b34t120odva5">
                    {UGC_SKILLS.map(sk => <li key={sk.name} className="flex gap-2" data-id="element-9918" data-mp="10r7v5zamvhe"><span className="text-brand-dark leading-6" data-id="element-9919" data-mp="11sk3ds1po0p">•</span><span data-id="element-9920" data-mp="5yssvwga54vi"><strong className="font-semibold" data-id="element-9921" data-mp="5ystnyo3t8wk">{sk.name}</strong> — {sk.skill}</span></li>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Put it down anywhere — plain background ────────────── */}
        <section className="flex flex-col gap-8 md:gap-12 py-4 md:py-10" data-id="element-9922" data-mp="jqz89gblz7tw">
          <div className="flex flex-col items-center text-center gap-3 max-w-[64ch] mx-auto" data-id="element-9923" data-mp="jstmycwae3pj">
            <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-brand-dark" data-id="element-9924" data-mp="orwo3m95bps8">Portable by design</span>
            <h2 className="font-title font-extrabold text-3xl md:text-[44px] leading-[1.08] tracking-tight" data-id="element-9925" data-mp="ynznwlgizcc0">Put it down in ChatGPT, pick it up in <RotatingWord data-id="element-9926" data-mp="2vhbc2w8jhwzr" /></h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-9927" data-mp="2vi47x25yn234">
              Giga is its own system — it lives outside any single provider. Your credentials, skills,
              and files come with you, so you can work from anywhere and you're never locked in.
            </p>
          </div>

          {/* Providers → one shared Giga system */}
          <div className="flex flex-col items-stretch max-w-[760px] mx-auto w-full" data-id="element-9928" data-mp="32dmbg2w9dtr9">
            <div className="grid grid-cols-3 gap-3 md:gap-6" data-id="element-9929" data-mp="2xcq7jpbwq7lu">
              {PROVIDERS.map(p => <div key={p.name} className="flex flex-col items-center min-w-0" data-id="element-9930" data-mp="3bd0y0r9b1k6h">
                  <div className="w-full bg-white border border-sand-200 rounded-2xl shadow-sm p-3 md:p-4 flex items-center justify-center gap-2 md:gap-2.5" data-id="element-9931" data-mp="36eg1b1t9hims">
                    <img src={p.logo} alt="" className="w-5 h-5 md:w-6 md:h-6 flex-none" data-id="element-9932" data-mp="3l2strot54jcj" />
                    <span className="font-title font-bold text-[13px] md:text-[16px] truncate" data-id="element-9933" data-mp="3g4releyu546c">{p.name}</span>
                  </div>
                  <div className="w-0 h-8 md:h-10 border-l-2 border-dashed border-[#D6CCC0]" data-id="element-9934" data-mp="3u1o1j5bu21hy" />
                </div>)}
            </div>

            <div className="relative border-2 border-dashed border-[#D6CCC0] rounded-[28px] bg-sand-50 pt-11 px-4 md:px-8 pb-6 md:pb-8" data-id="element-9935" data-mp="cktjdevkrhxv">
              <div className="absolute -top-3.5 left-6 md:left-8 flex items-center gap-2 bg-ink text-white rounded-full py-1.5 pl-3 pr-4" data-id="element-9936" data-mp="hjwjckwp6xyz">
                <Sparkles className="w-3.5 h-3.5 text-brand-lighter" data-id="element-9937" data-mp="8jq9d907kg4k" />
                <span className="font-mono text-[11px] font-semibold tracking-[0.14em] uppercase" data-id="element-9938" data-mp="8lukaawiyfec">Giga · your company brain</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4" data-id="element-9939" data-mp="3ur7yvi9hrzyd">
                {SYSTEM_BOXES.map(({
                icon: Icon,
                name,
                desc
              }) => <div key={name} className="bg-white border border-sand-200 rounded-2xl p-4 flex flex-col gap-2" data-id="element-9940" data-mp="3l0n4k70uphl9">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-brand/10 text-brand-dark" data-id="element-9941" data-mp="372ez1apr17yh"><Icon className="w-5 h-5" data-id="element-9942" data-mp="3bzf5vnufaeqn" /></span>
                    <span className="font-title font-bold text-[15px]" data-id="element-9943" data-mp="i8h48ammcf87">{name}</span>
                    <span className="text-[12.5px] leading-[1.45] text-ink-muted" data-id="element-9944" data-mp="ial3ugdhis2o">{desc}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* ── Built for teams ───────────────────────────────────── */}
        <section className={`${CARD} gap-8 md:gap-10`} data-id="element-9945" data-mp="4g0u6pvavgxs">
          <div className="flex flex-col items-center text-center gap-3 max-w-[60ch] mx-auto" data-id="element-9946" data-mp="3vndgtkeycml3">
            <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-brand-dark" data-id="element-9947" data-mp="3qoa8nr3oe08w">Connections</span>
            <h2 className="font-title font-extrabold text-3xl md:text-[42px] leading-[1.05] tracking-tight" data-id="element-9948" data-mp="3mmf7qnpnpovn">Built for teams.</h2>
            <p className="text-base md:text-lg leading-relaxed text-ink-muted text-pretty" data-id="element-9949" data-mp="3honik1gecgv5">
              Connections, files, skills — share any of it exactly where it belongs: a whole group,
              a single project space, or one person. No re-entering keys, no shared passwords.
            </p>
          </div>

          {/* Sharing visual — a credential / file / skill, shared where it belongs */}
          <TeamSharingVisual data-id="element-9950" data-mp="1sdjunkzfnihd" />
        </section>

        {/* ── Get started — auto-playing install walkthrough ────── */}
        {/* On ink, like the closing panel on the home page, the pricing page
         and every feature page. The page previously ended on the same cream it
         opened with and a small pill floating in it, so it trailed off rather
         than closing. */}
        <section className="relative isolate mt-2 flex flex-col items-center gap-8 overflow-hidden rounded-[22px] bg-espresso px-6 py-14 text-white md:gap-10 md:px-12 md:py-20" data-id="element-9951" data-mp="19h1wakq2wkc5">
          <div aria-hidden="true" className="dot-matrix-dark pointer-events-none absolute inset-0 opacity-40" data-id="element-9952" data-mp="1ih6rzxlwtuah" />
          <div className="relative flex max-w-[56ch] flex-col items-center gap-3 text-center" data-id="element-9953" data-mp="2pv0cvi9esbwg">
            <h2 className="font-title text-3xl font-extrabold leading-[1.05] tracking-tight text-[#FAF8F4] md:text-[42px]" data-id="element-9954" data-mp="2tv2j84q39ti2">Get started in 30 seconds</h2>
            <p className="text-pretty text-base leading-relaxed text-white/60 md:text-lg" data-id="element-9955" data-mp="ut6r5h8an1a9">Installing Giga is as simple as going to plugins.</p>
          </div>
          <div className="relative w-full" data-id="element-9956" data-mp="13qpi128mc9u2">
            <GetStartedFlow data-id="element-9957" data-mp="13pedv1b9h02f" />
          </div>
          <a href="https://chatgpt.com/plugins?q=giga" target="_blank" rel="noopener noreferrer" className="relative inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-[15px] font-bold text-[#2B211A] transition-[background-color,transform] duration-200 ease-out hover:-translate-y-px hover:bg-[#F5EFE7]" data-id="element-9958" data-mp="ysflan78x16c">
            Get started <ArrowRight className="w-4 h-4" data-id="element-9959" data-mp="ku7ihynj35n9" />
          </a>
        </section>

      </div>
    </div>;
}